/**
 * Appeal (Week 6-7) and Outcome (Week 8) records.
 *
 * Implements rules in CLAUDE.md §G.3 and §G.4:
 *   - Appeal must be filed within 2 days after employee receives the result
 *   - HR facilitates the meeting and issues the final decision
 *   - Outcome paths: increment / promotion / PIP / no_change
 */

import type { Timestamp } from 'firebase/firestore';

// ─── Appeal record (§G.3) ───────────────────────────────────────────────────

export type AppealStatus = 'open' | 'in_meeting' | 'decided';

export type AppealDecision =
  | 'upheld_original'      // appraisal stands as-is
  | 'partially_adjusted'   // some changes accepted, recorded as new calibration adjustment
  | 'fully_revised';       // appraisal rewritten

/** SLA window per CLAUDE.md §G.3: 2 days. Used to compute deadline server-side. */
export const APPEAL_FILING_WINDOW_DAYS = 2;

export interface AppealAttendee {
  userId: string;
  name: string;
  role: 'employee' | 'manager' | 'hr' | 'peer';
}

export interface AppealRecord {
  id: string;
  cycleId: string;
  employeeId: string;
  appraisalId: string;

  filedAt: Timestamp;
  /** Server-validated against APPEAL_FILING_WINDOW_DAYS. */
  filedWithinWindow: boolean;
  filedReason: string;

  status: AppealStatus;

  meetingScheduledAt?: Timestamp;
  attendees?: AppealAttendee[];

  decision?: AppealDecision;
  decisionBy?: string;          // HR userId
  decidedAt?: Timestamp;
  decisionNotes?: string;

  createdAt: Timestamp;
  updatedAt?: Timestamp;
}

// ─── Outcome record (§G.4) ──────────────────────────────────────────────────

export type OutcomeType =
  | 'increment'
  | 'promotion'
  | 'increment_and_promotion'
  | 'pip'
  | 'no_change';

export interface OutcomeRecord {
  id: string;
  cycleId: string;
  employeeId: string;
  appraisalId: string;

  outcomeType: OutcomeType;

  // increment / promotion payload
  incrementAmount?: number;       // absolute or % — HR convention determined at use site
  incrementCurrency?: string;
  promotionToTitle?: string;
  promotionToGrade?: string;
  effectiveDate?: string;         // ISO date

  /**
   * Free-form pointer to the PIP module record. The PIP module itself is out
   * of scope for this repo per CLAUDE.md §G.4.
   */
  pipFormRef?: string;

  /** Free-form pointer to the contract addendum document. */
  contractAddendumRef?: string;

  processedBy: string;
  processedAt: Timestamp;
  notes?: string;
}
